import { Box, Flex, Text } from '@chakra-ui/react';
import { msg } from '@lingui/core/macro';
import { useLingui } from '@lingui/react';
import { useEffect, useState } from 'react';

import { thinScrollbar } from '#features/ui';
import { BaseLink } from '#features/ui/components/Link';

interface TocItem {
  id: string;
  text: string;
  level: number;
}

interface TableOfContentsProps {
  items: TocItem[];
  variant?: 'sidebar' | 'inline';
}

export type { TocItem };

const TocLink = ({
  item,
  isActive,
  compact,
}: {
  item: TocItem;
  isActive: boolean;
  compact?: boolean;
}) => (
  <BaseLink
    display="block"
    href={`#${item.id}`}
    onClick={(e) => {
      e.preventDefault();
      const el = document.getElementById(item.id);
      if (el) {
        const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        el.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
      }
    }}
    aria-current={isActive ? 'location' : undefined}
    textDecoration="none"
    _hover={{ textDecoration: 'none' }}
  >
    <Text
      pl={item.level === 3 ? 6 : 3}
      py={1}
      minH={compact ? '44px' : undefined}
      display={compact ? 'flex' : undefined}
      alignItems={compact ? 'center' : undefined}
      textStyle={isActive ? 'title' : 'body'}
      color={isActive ? 'fg.emphasized' : 'fg.secondary'}
      overflowWrap="anywhere"
      borderLeftWidth="2px"
      borderColor={isActive ? 'fg.link' : 'transparent'}
      ml="-1px"
      transition="color var(--chakra-durations-fast) var(--chakra-easings-ease-out), border-color var(--chakra-durations-fast) var(--chakra-easings-ease-out)"
      _hover={{ color: 'fg.emphasized' }}
      css={{ '@media (prefers-reduced-motion: reduce)': { transition: 'none' } }}
    >
      {item.text}
    </Text>
  </BaseLink>
);

export const TableOfContents = ({ items, variant = 'sidebar' }: TableOfContentsProps) => {
  const { _ } = useLingui();
  const label = _(msg({ id: 'blog.contents', message: 'Contents' }));
  const [activeId, setActiveId] = useState<string>('');

  useEffect(() => {
    if (items.length === 0 || typeof IntersectionObserver === 'undefined') return;

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setActiveId(entry.target.id);
          }
        }
      },
      { rootMargin: '-80px 0px -60% 0px', threshold: 0 }
    );

    for (const item of items) {
      const el = document.getElementById(item.id);
      if (el) observer.observe(el);
    }

    return () => observer.disconnect();
  }, [items]);

  if (items.length === 0) return null;

  const links = (
    <Flex direction="column" gap={1} borderLeftWidth="1px" borderColor="border">
      {items.map((item) => (
        <TocLink
          key={item.id}
          item={item}
          isActive={activeId === item.id}
          compact={variant === 'inline'}
        />
      ))}
    </Flex>
  );

  if (variant === 'inline') {
    return (
      <Box as="nav" aria-label={label} display={{ base: 'block', xl: 'none' }} maxW="720px" mb={6}>
        <Box as="details">
          <Box
            as="summary"
            textStyle="title"
            color="fg.emphasized"
            minH="44px"
            py={3}
            cursor="pointer"
            _focusVisible={{
              outline: '2px solid',
              outlineColor: 'purple.focusRing',
              outlineOffset: '2px',
            }}
          >
            {label}
          </Box>
          {links}
        </Box>
      </Box>
    );
  }

  return (
    <Box
      as="nav"
      aria-label={label}
      position="sticky"
      top="100px"
      maxH="calc(100dvh - 124px)"
      overflowY="auto"
      css={thinScrollbar}
      w="220px"
      flexShrink={0}
      display={{ base: 'none', xl: 'block' }}
    >
      <Text textStyle="title" color="fg.muted" mb={3}>
        {label}
      </Text>
      {links}
    </Box>
  );
};
